import * as i0 from '@angular/core';
import { Injectable, Component, ChangeDetectionStrategy, Input, HostBinding, NgModule } from '@angular/core';
import * as i1 from '@angular/platform-browser';

/**
 * Hyphenated to UpperCamelCase
 */
function toUpperCamelCase(str) {
    return toCapitalCase(toPropertyName(str));
}
/**
 * Hyphenated to lowerCamelCase
 */
function toPropertyName(str) {
    return str
        .replace(/([^a-zA-Z0-9])+(.)?/g, (_, __, chr) => chr ? chr.toUpperCase() : '')
        .replace(/[^a-zA-Z\d]/g, '')
        .replace(/^([A-Z])/, m => m.toLowerCase());
}
/**
 * Capitalizes the first letter of a string
 */
function toCapitalCase(str) {
    return str.charAt(0).toUpperCase() + str.substr(1);
}

class IconService {
    /** Access the icons in the class. */
    get icons() {
        return IconService.icons;
    }
    /**
     * Insert icons into the iconset
     */
    static addIcons(icons) {
        IconService.icons = Object.assign(Object.assign({}, IconService.icons), icons);
    }
}
/** Store the available icons. */
IconService.icons = {};
IconService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: IconService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
IconService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: IconService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: IconService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class IconComponent {
    constructor(elementRef, sanitizer, iconService) {
        this.elementRef = elementRef;
        this.sanitizer = sanitizer;
        this.iconService = iconService;
        this._size = '1em';
    }
    /** Define the name of the icon to display */
    set name(name) {
        name = toUpperCamelCase(name);
        // if there is no icon with this name warn the user as they probably forgot to import it
        if (!this.iconService.icons.hasOwnProperty(name)) {
            console.warn(`No icon named ${name} was found. You may need to import it using the withIcons function.`);
            return;
        }
        // insert the SVG into the template
        this.template = this.sanitizer.bypassSecurityTrustHtml(this.iconService.icons[name]);
    }
    /** Define the size of the icon */
    set size(size) {
        // if the size only contains numbers, assume it is in pixels
        this._size = coerceCssPixelValue(size);
    }
    get size() {
        return this._size;
    }
}
IconComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: IconComponent, deps: [{ token: i0.ElementRef }, { token: i1.DomSanitizer }, { token: IconService }], target: i0.ɵɵFactoryTarget.Component });
IconComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: IconComponent, selector: "ng-icon", inputs: { name: "name", size: "size", strokeWidth: "strokeWidth", color: "color" }, host: { properties: { "innerHTML": "this.template", "style.--ng-icon__size": "this.size", "style.--ng-icon__stroke-width": "this.strokeWidth", "style.color": "this.color" } }, ngImport: i0, template: '', isInline: true, styles: [":host{display:inline-block;width:var(--ng-icon__size);height:var(--ng-icon__size)}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: IconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ng-icon', template: '', changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:inline-block;width:var(--ng-icon__size);height:var(--ng-icon__size)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.DomSanitizer }, { type: IconService }]; }, propDecorators: { name: [{
                type: Input
            }], template: [{
                type: HostBinding,
                args: ['innerHTML']
            }], size: [{
                type: HostBinding,
                args: ['style.--ng-icon__size']
            }, {
                type: Input
            }], strokeWidth: [{
                type: HostBinding,
                args: ['style.--ng-icon__stroke-width']
            }, {
                type: Input
            }], color: [{
                type: HostBinding,
                args: ['style.color']
            }, {
                type: Input
            }] } });
function coerceCssPixelValue(value) {
    if (value == null) {
        return '';
    }
    return /^\d+$/.test(value) ? `${value}px` : value;
}

class NgIconsModule {
    constructor(iconService) {
        this.iconService = iconService;
        if (Object.keys(this.iconService.icons).length === 0) {
            throw new Error('No icons have been provided. Ensure to include some icons by importing them using NgIconsModule.withIcons({ ... }).');
        }
    }
    /**
     * Define the icons that will be included in the application. This allows unused icons to
     * be tree-shaken away to reduce bundle size
     * @param icons The object containing the required icons
     */
    static withIcons(icons) {
        IconService.addIcons(icons);
        return { ngModule: NgIconsModule };
    }
}
NgIconsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NgIconsModule, deps: [{ token: IconService }], target: i0.ɵɵFactoryTarget.NgModule });
NgIconsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NgIconsModule, declarations: [IconComponent], exports: [IconComponent] });
NgIconsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NgIconsModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: NgIconsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [IconComponent],
                    exports: [IconComponent],
                }]
        }], ctorParameters: function () { return [{ type: IconService }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { IconComponent, NgIconsModule };
